import { captureException } from '@sentry/react-router'
import { useEffect, type ReactElement } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from 'react-router'
import { getErrorMessage } from '#app/utils/misc'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

/**
 * Boundary for routes that call `enforceAbuseLimit`. Mount it on a route
 * *below* the dashboard shell: a 429 then replaces that page's content and
 * leaves the sidebar, so the user can walk away from the wall instead of
 * watching the whole application disappear for 60 seconds.
 */
export function RateLimitBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				429: ({ error }) => {
					const retryAfter =
						typeof error.data === 'object' &&
						error.data !== null &&
						'retryAfter' in error.data
							? Number(error.data.retryAfter)
							: null
					return (
						<div className="max-w-md text-left">
							<p className="text-warning-strong eyebrow">/ too many attempts</p>
							<h1 className="mt-2 font-[family-name:var(--font-display)] text-[24px] leading-[1.15] font-semibold tracking-[-0.01em]">
								Slow down for a moment.
							</h1>
							<p className="text-muted-foreground mt-2 text-[13px] leading-relaxed font-normal">
								{retryAfter
									? `Too many tries in a row. This page accepts another in ${retryAfter} seconds.`
									: 'Too many tries in a row. Give this page a minute before trying again.'}{' '}
								Nothing you entered was saved, and nothing changed.
							</p>
						</div>
					)
				},
			}}
		/>
	)
}

export function GeneralErrorBoundary({
	// `error.data` is whatever the throwing loader/action passed to `data()`.
	// Rendering it raw crashed React ("Objects are not valid as a React child")
	// for every route that throws a structured body - `enforceAbuseLimit` throws
	// `{error, message, retryAfter}`, so hitting a rate limit replaced the page
	// with a React error instead of the message it had already written.
	defaultStatusHandler = ({ error }) => (
		<p>
			{error.status}{' '}
			{typeof error.data === 'string'
				? error.data
				: getErrorMessage(error.data)}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler = (error) => <p>{getErrorMessage(error)}</p>,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (isResponse) return

		captureException(error)
	}, [error, isResponse])

	return (
		<div className="text-h2 container flex items-center justify-center p-20">
			{isResponse
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	)
}
